import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
} from 'firebase/firestore'
import { getDb } from './firebase'
import { ADMIN_EMAILS } from '../../auth/firebase'

export type UserRole = 'admin' | 'editor' | 'viewer'

export interface SlideUser {
  uid: string
  email: string
  displayName: string
  photoURL?: string | null
  role: UserRole
  createdAt?: string
  lastLoginAt?: string
}

const ROLES_COLLECTION = 'presentations'
const ROLES_DOC_ID = '_user_roles'
const USERS_COLLECTION = 'users'
const LOCAL_USERS_CACHE_KEY = 'mamf_slide_users_cache'

/**
 * Pre-registered MAMF workspace accounts from Firebase Authentication
 */
export const KNOWN_MAMF_USERS: SlideUser[] = [
  {
    uid: 'N7Ul3wdbg5cq1r2I6QDD4Hc',
    email: 'jakub.nagiet@mamf.pl',
    displayName: 'Jakub Nagiet',
    photoURL: null,
    role: 'admin',
    createdAt: '2026-09-05T10:00:00.000Z',
    lastLoginAt: '2026-09-14T11:00:00.000Z',
  },
  {
    uid: 'EN9bk3R9KMmomdcZID68U4',
    email: 'agnieszka.krzysztofik@mamf.pl',
    displayName: 'Agnieszka Krzysztofik',
    photoURL: null,
    role: 'editor',
    createdAt: '2026-09-08T09:00:00.000Z',
    lastLoginAt: '2026-09-08T09:00:00.000Z',
  },
  {
    uid: 'ljUH3bT4DiaWOuz97XY58TQ0',
    email: 'krystyna.kulisz@mamf.pl',
    displayName: 'Krystyna Kulisz',
    photoURL: null,
    role: 'viewer',
    createdAt: '2026-09-08T09:30:00.000Z',
    lastLoginAt: '2026-09-08T09:30:00.000Z',
  },
]

// In-memory subscribers for real-time local updates
const activeSubscribers = new Set<(users: SlideUser[]) => void>()

const notifySubscribers = (users: SlideUser[]) => {
  activeSubscribers.forEach((cb) => {
    try {
      cb(users)
    } catch {}
  })
}

/**
 * Checks if a given email is a predefined superadmin
 */
export const isPredefinedAdminEmail = (email?: string | null): boolean => {
  if (!email) return false
  const clean = email.trim().toLowerCase()
  return ADMIN_EMAILS.includes(clean)
}

export const mergeWithKnownUsers = (
  existingList: SlideUser[],
  rolesMap?: Record<string, UserRole>
): SlideUser[] => {
  const map = new Map<string, SlideUser>()

  // 1. Known users as baseline
  KNOWN_MAMF_USERS.forEach((u) => {
    const key = u.email.toLowerCase()
    const roleOverride =
      rolesMap?.[u.uid] || rolesMap?.[key] || u.role
    map.set(key, {
      ...u,
      role: isPredefinedAdminEmail(u.email) ? 'admin' : roleOverride,
    })
  })

  // 2. Overlay existing list
  existingList.forEach((u) => {
    const key = u.email.toLowerCase()
    const roleOverride =
      rolesMap?.[u.uid] || rolesMap?.[key] || u.role
    const current = map.get(key)
    if (current) {
      map.set(key, {
        ...current,
        ...u,
        role: isPredefinedAdminEmail(u.email) ? 'admin' : (roleOverride || current.role),
      })
    } else {
      map.set(key, {
        ...u,
        role: isPredefinedAdminEmail(u.email) ? 'admin' : (roleOverride || 'viewer'),
      })
    }
  })

  const merged = Array.from(map.values())

  // Sort: Admins first, then editors, then viewers; then alphabetically
  merged.sort((a, b) => {
    const roleWeight = { admin: 3, editor: 2, viewer: 1 }
    const diff = (roleWeight[b.role] || 0) - (roleWeight[a.role] || 0)
    if (diff !== 0) return diff
    return a.displayName.localeCompare(b.displayName)
  })

  return merged
}

export const getCachedUsers = (): SlideUser[] => {
  if (typeof window === 'undefined') return KNOWN_MAMF_USERS
  try {
    const raw = localStorage.getItem(LOCAL_USERS_CACHE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length > 0) {
        return mergeWithKnownUsers(parsed)
      }
    }
  } catch {}
  return mergeWithKnownUsers([])
}

export const setCachedUsers = (users: SlideUser[]): void => {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(LOCAL_USERS_CACHE_KEY, JSON.stringify(users))
    window.dispatchEvent(new CustomEvent('mamf-user-roles-updated', { detail: { users } }))
  } catch {}
}

/**
 * Synchronizes or registers a user record in Firestore on login
 */
export const syncUserToFirestore = async (user: {
  uid: string
  email: string
  displayName: string
  photoURL?: string | null
}): Promise<SlideUser> => {
  const cleanEmail = (user.email || '').trim().toLowerCase()
  const isPredefinedAdmin = isPredefinedAdminEmail(cleanEmail)

  // Default fallback user
  const fallbackUser: SlideUser = {
    uid: user.uid,
    email: cleanEmail,
    displayName: user.displayName || cleanEmail.split('@')[0] || 'User',
    photoURL: user.photoURL || null,
    role: isPredefinedAdmin ? 'admin' : 'viewer',
    createdAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
  }

  const db = getDb()
  if (!db) {
    const merged = mergeWithKnownUsers([fallbackUser, ...getCachedUsers()])
    setCachedUsers(merged)
    notifySubscribers(merged)
    return fallbackUser
  }

  try {
    // Read persisted roles from presentations/_user_roles (guaranteed allowed by Firestore rules)
    const rolesDocRef = doc(db, ROLES_COLLECTION, ROLES_DOC_ID)
    const rolesSnap = await getDoc(rolesDocRef)
    const rolesData = rolesSnap.exists() ? (rolesSnap.data() as any) : null
    const existingRoles: Record<string, UserRole> = rolesData?.roles || {}

    const assignedRole: UserRole = isPredefinedAdmin
      ? 'admin'
      : existingRoles[user.uid] || existingRoles[cleanEmail] || 'viewer'

    fallbackUser.role = assignedRole

    // Update roles document with user details
    const currentUsers = rolesData?.users && Array.isArray(rolesData.users) ? rolesData.users : []
    const updatedUsers = mergeWithKnownUsers([fallbackUser, ...currentUsers], existingRoles)

    await setDoc(
      rolesDocRef,
      {
        _isSystem: true,
        _deleted: false,
        roles: {
          ...existingRoles,
          [user.uid]: assignedRole,
          [cleanEmail]: assignedRole,
        },
        users: updatedUsers,
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    )

    // Also attempt users collection write (silent fallback if denied)
    try {
      const userDocRef = doc(db, USERS_COLLECTION, user.uid)
      await setDoc(userDocRef, fallbackUser, { merge: true })
    } catch {}

    setCachedUsers(updatedUsers)
    notifySubscribers(updatedUsers)

    return fallbackUser
  } catch (err) {
    console.warn('User sync fallback to local cache:', err)
    const merged = mergeWithKnownUsers([fallbackUser, ...getCachedUsers()])
    setCachedUsers(merged)
    notifySubscribers(merged)
    return fallbackUser
  }
}

/**
 * Subscribes to real-time updates for all MAMF slide users
 */
export const subscribeSlideUsers = (
  onUpdate: (users: SlideUser[]) => void
): (() => void) => {
  activeSubscribers.add(onUpdate)

  // Immediately push cached users
  const initial = getCachedUsers()
  onUpdate(initial)

  const db = getDb()
  if (!db) {
    return () => {
      activeSubscribers.delete(onUpdate)
    }
  }

  try {
    // Listen to presentations/_user_roles (guaranteed permission allowed)
    const rolesDocRef = doc(db, ROLES_COLLECTION, ROLES_DOC_ID)
    const unsubRoles = onSnapshot(
      rolesDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as any
          const rolesMap: Record<string, UserRole> = data?.roles || {}
          const storedUsers: SlideUser[] = Array.isArray(data?.users) ? data.users : []
          const merged = mergeWithKnownUsers(storedUsers, rolesMap)
          setCachedUsers(merged)
          notifySubscribers(merged)
        } else {
          // Initialize roles document in Firestore if it doesn't exist yet
          const baseline = getCachedUsers()
          const rolesMap: Record<string, UserRole> = {}
          baseline.forEach((u) => {
            rolesMap[u.uid] = u.role
            rolesMap[u.email.toLowerCase()] = u.role
          })
          setDoc(
            rolesDocRef,
            {
              _isSystem: true,
              _deleted: false,
              roles: rolesMap,
              users: baseline,
              updatedAt: new Date().toISOString(),
            },
            { merge: true }
          ).catch(() => {})
        }
      },
      (err) => {
        console.warn('Roles listener error:', err)
        onUpdate(getCachedUsers())
      }
    )

    return () => {
      activeSubscribers.delete(onUpdate)
      unsubRoles()
    }
  } catch (err) {
    console.warn('Failed to subscribe to slide users:', err)
    return () => {
      activeSubscribers.delete(onUpdate)
    }
  }
}

/**
 * Subscribes to role changes for a specific user
 */
export const subscribeUserRole = (
  uid: string,
  email: string,
  onRoleChange: (role: UserRole) => void
): (() => void) => {
  const cleanEmail = email.toLowerCase()
  if (isPredefinedAdminEmail(cleanEmail)) {
    onRoleChange('admin')
    return () => {}
  }

  const db = getDb()
  if (!db) return () => {}

  try {
    const rolesDocRef = doc(db, ROLES_COLLECTION, ROLES_DOC_ID)
    return onSnapshot(rolesDocRef, (snap) => {
      if (snap.exists()) {
        const roles = snap.data()?.roles || {}
        const newRole: UserRole = roles[uid] || roles[cleanEmail] || 'viewer'
        onRoleChange(newRole)
      }
    })
  } catch {
    return () => {}
  }
}

/**
 * Updates a user's role in Firestore and local cache
 */
export const updateSlideUserRole = async (
  uid: string,
  newRole: UserRole,
  email?: string
): Promise<void> => {
  const targetEmail = (email || '').toLowerCase().trim()

  // 1. Update cache & notify subscribers immediately (optimistic UI)
  const current = getCachedUsers().map((u) => {
    if (u.uid === uid || (targetEmail && u.email.toLowerCase() === targetEmail)) {
      return { ...u, role: newRole }
    }
    return u
  })
  setCachedUsers(current)
  notifySubscribers(current)

  const db = getDb()
  if (!db) return

  const targetUser = current.find(
    (u) => u.uid === uid || (targetEmail && u.email.toLowerCase() === targetEmail)
  )
  const finalEmail = targetEmail || (targetUser?.email ? targetUser.email.toLowerCase() : '')
  const targetUid = targetUser?.uid || uid

  // 2. Persist to guaranteed accessible presentations/_user_roles
  try {
    const rolesDocRef = doc(db, ROLES_COLLECTION, ROLES_DOC_ID)
    const snap = await getDoc(rolesDocRef)
    const existingRoles: Record<string, UserRole> = snap.exists() ? (snap.data()?.roles || {}) : {}

    const updatedRoles: Record<string, UserRole> = {
      ...existingRoles,
      [targetUid]: newRole,
      ...(finalEmail ? { [finalEmail]: newRole } : {}),
    }

    await setDoc(
      rolesDocRef,
      {
        _isSystem: true,
        _deleted: false,
        roles: updatedRoles,
        users: current,
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    )
  } catch (rolesErr) {
    console.warn('Failed to update role in presentations/_user_roles:', rolesErr)
  }

  // 3. Also attempt direct users collection write if allowed
  try {
    const userDocRef = doc(db, USERS_COLLECTION, targetUid)
    await setDoc(
      userDocRef,
      {
        role: newRole,
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    )
  } catch {
    // Ignore permission error if /users is not permitted
  }
}
